import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const SMSFSearchBar = ({ tabs, onNavigate, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);

    // Searchable content
    const searchableContent = tabs.map(tab => ({
        ...tab,
        keywords: generateKeywords(tab.id, tab.label)
    }));

    useEffect(() => {
        if (searchTerm.length >= 2) {
            const filtered = searchableContent.filter(item => {
                const term = searchTerm.toLowerCase();
                return (
                    item.label.toLowerCase().includes(term) ||
                    item.keywords.some(keyword => keyword.includes(term))
                );
            });
            setResults(filtered.slice(0, 8)); // Top 8 results
            setSelectedIndex(0);
        } else {
            setResults([]);
        }
    }, [searchTerm]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && results.length > 0) {
            e.preventDefault();
            handleSelect(results[selectedIndex]);
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const handleSelect = (tab) => {
        onNavigate(tab.id);
        onClose();
        setSearchTerm('');
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Search Input */}
                <div className="p-4 border-b">
                    <div className="flex items-center">
                        <span className="text-2xl mr-3">🔍</span>
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search calculators... (try 'contribution', 'pension', 'tax')"
                            className="flex-1 text-lg px-3 py-2 focus:outline-none"
                        />
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 ml-2"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Results */}
                <div className="max-h-96 overflow-y-auto">
                    {results.length > 0 ? (
                        <div className="p-2">
                            {results.map((tab, index) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleSelect(tab)}
                                    className={`w-full flex items-center p-3 rounded-lg mb-1 transition ${
                                        index === selectedIndex
                                            ? 'bg-blue-100 border-2 border-blue-500'
                                            : 'bg-white hover:bg-gray-50 border-2 border-transparent'
                                    }`}
                                >
                                    <span className="text-2xl mr-3">{tab.icon}</span>
                                    <div className="flex-1 text-left">
                                        <div className="font-medium text-gray-900">{tab.label}</div>
                                        <div className="text-xs text-gray-500">
                                            {tab.keywords.slice(0, 3).join(' • ')}
                                        </div>
                                    </div>
                                    <span className="text-gray-400">→</span>
                                </button>
                            ))}
                        </div>
                    ) : searchTerm.length >= 2 ? (
                        <div className="p-8 text-center text-gray-500">
                            <p>No calculators found</p>
                            <p className="text-sm mt-2">Try different keywords</p>
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <p>Type to search calculators</p>
                            <p className="text-sm mt-2">Press ↑↓ to navigate, Enter to select, Esc to close</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Generate searchable keywords for each calculator
function generateKeywords(id, label) {
    const keywordMap = {
        'carry_forward': ['contribution', 'concessional', 'cap', 'unused', '5 year', 'catch up', 'carryforward'],
        'bring_forward': ['$360k', 'non-concessional', 'contribution', '3 year', 'lump sum', 'NCC', 'bringforward'],
        'co_contribution': ['government', 'contribution', 'free money', '$500', 'low income', 'cocontribution'],
        'downsizer': ['contribution', 'home sale', '$300k', 'property', 'age 55', 'downsize', 'house'],
        'division293': ['contribution', 'high income', '$250k', 'extra tax', '15%', 'div 293', 'division 293'],
        'work_test': ['40 hours', 'age 67', 'employment', 'eligibility', 'contribution'],
        'fhss': ['first home', 'house deposit', '$50k', 'young', 'contribution', 'first home super saver'],
        'spouse_split': ['spouse', 'partner', 'equalize', 'family', 'optimize', 'contribution', 'splitting'],
        'lrba': ['borrowing', 'property loan', 'LVR', '80%', 'limited recourse'],
        'limits_monitor': ['in-house', '5%', 'compliance', 'real-time', 'investment limits', 'related party'],
        'pension_optimizer': ['pension', 'retirement income', 'drawdown', 'tax-free', 'TBC', 'transfer balance'],
        'tax_harvesting': ['capital gains', 'losses', 'CGT', 'tax loss'],
        'cashflow': ['projection', 'retirement', 'income', 'expenses', 'cash flow'],
        'insurance': ['life', 'TPD', 'income protection', 'cover'],
        'estate': ['death benefit', 'will', 'beneficiary', 'estate planning'],
        'succession': ['nomination', 'reversionary', 'dependant', 'successor'],
        'compliance': ['ATO', 'audit', 'rules', 'check', 'enhanced compliance', 'administration'],
        'enhanced_compliance': ['compliance', 'ATO', 'enhanced', 'real-time', 'breach'],
        'administration': ['compliance', 'admin', 'management', 'documents', 'minutes'],
        'reports': ['PDF', 'export', 'document', 'ATO', 'reporting'],
        'email_reports': ['automate', 'send', 'schedule', 'delivery', 'email'],
        'comparison': ['industry fund', 'fees', 'vs', 'decide', 'SMSF vs'],
        'dashboard': ['overview', 'summary', 'at-a-glance', 'status'],
        'wizard': ['setup', 'getting started', 'tutorial', 'guide'],
        'configuration': ['personal', 'fund details', 'setup', 'ABN', 'trustee'],
        'investment': ['strategy', 'allocation', 'portfolio', 'asset mix'],
        'collectibles': ['art', 'wine', 'cars', 'collectible', 'storage'],
        'related_party': ['in-house', '5%', 'related party', 'monitor'],
        'valuation': ['asset', 'property', 'value', 'market'],
        'analytics': ['performance', 'analysis', 'advanced', 'metrics'],
        'risk': ['assessment', 'risk', 'tolerance', 'profile'],
        'performance': ['optimization', 'returns', 'performance', 'improvement'],
        'history': ['trends', 'historical', 'tracking', 'data'],
        'data': ['export', 'import', 'backup', 'CSV', 'JSON'],
        'strategy': ['builder', 'advanced', 'custom', 'backtesting'],
        'enhanced': ['features', 'advanced', 'AI', 'automation']
    };

    return keywordMap[id] || [label.toLowerCase()];
}

SMSFSearchBar.propTypes = {
    tabs: PropTypes.array.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default SMSFSearchBar;

